import React, { useState, useEffect, useCallback, useRef } from 'react';
import '../../../popup/stations/Station.scss';
import { ONEDAY, backgroundColor, roundOneDecimal } from '../../../../../store/common';
import { useStoreContext } from '../../../../../store/context';
import moment from 'moment-timezone';
import { herbFMColors, hunDeadFuelColors, noColors, oneDeadFuelColors, tenDeadFuelColors, thouDeadFuelColors, woodyFMColors, gsiColors, kbdiColors } from '../../../../../constants';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import { useAddAlert } from 'src/store/actions';
import { Button, TableHead } from '@mui/material';
import { Grid } from 'src/shared/grid';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import { getNfdrMinMaxInclTime } from 'src/apis/getNfdrsCalls';
import LaunchIcon from '@mui/icons-material/Launch';
import { ROUTE_PATHS } from 'src/routes';

type DailyTablePopUpProps = {
  id:any;
  stationMeta: any;
}

export const NfdrDailyTable: React.FC<DailyTablePopUpProps> = ({
  id, stationMeta
}) => {
  const [state, dispatch] = useStoreContext();
  const [modifiedContents, setModifiedContents] = useState<any[]>([]);
  const addAlert = useAddAlert(dispatch);
  const scrollRef = useRef<HTMLTableElement | HTMLDivElement | null>(null);
  const [ nfdrsDailyFailCount, setNfdrsDailyFailCount ] = useState<number>(0)

  const fillMissingDates = useCallback((data: any[], startTime: any, endTime: any)=>{
    const filledData:any[] = [];
    let currentTime = moment.utc(startTime);
    const endMoment = moment.utc(endTime);

    if(stationMeta?.time_zone_offset){
      while(currentTime<=endMoment){
        const currentHour = currentTime.toISOString();
        const existingData:any = data.find(item=>item.summary_date === moment(currentHour).utc().format('YYYY-MM-DD'));
        if(existingData){
          filledData.push(existingData);
        }
        else{
          filledData.push({
            station_id:stationMeta.station_id,
            station_name: stationMeta.station_name,
            summary_date:moment(currentTime).utc().format('YYYY-MM-DD'),
            nfdr_type: moment()>currentTime? 'O':'F',
            one_hr_tl_fuel_moisture_min: null,
            ten_hr_tl_fuel_moisture_min :null,
            hun_hr_tl_fuel_moisture_min: null,
            thou_hr_tl_fuel_moisture_min: null,
            ignition_component_max:null,
            ignition_component_max_time:null,
            spread_component_max: null,
            spread_component_max_time: null,
            energy_release_component_max: null,
            energy_release_component_max_time: null,
            burning_index_max:null,
            burning_index_max_time:null,
            herbaceous_lfi_fuel_moisture:null,
            woody_lfi_fuel_moisture:null,
            gsi: null,
            kbdi: null
          })
        }
        currentTime.add(1, 'day');
      }
    }
    return filledData;
  },[stationMeta]);

  const nfdrMinMaxCallback = useCallback((counter:number) => {
    (async () => {
      try {
        const currentMinus3 = moment(state.currentSliderDate -(3*ONEDAY)).utc().format('YYYY-MM-DD');
        const currentPlus3 =moment(state.currentSliderDate +(3*ONEDAY)).utc().format('YYYY-MM-DD');
        const varRequest = {
          stationIds: id.toString(),
          zoomLevel: 5,
          startDate: currentMinus3,
          endDate: currentPlus3,
          fuelModel: state.nfdrFuelModel,
          page: 0,
          perPage: 300000,
        };
        const response: any = await getNfdrMinMaxInclTime(varRequest, addAlert, state?.user);
        if (response.data?.length>0) {
          setNfdrsDailyFailCount(0)
          const sortedArray = response.data.sort((a:any, b:any) => moment(a.summary_date).valueOf() - moment(b.summary_date).valueOf());
          const responseLastObj = sortedArray[sortedArray.length-1]?.summary_date
          const forecastUsualEnd =moment().add(7,'days').utc().format('YYYY-MM-DD')

          let fillDates: any;
          if (moment(forecastUsualEnd) >= moment(currentPlus3)) {
            if (moment(responseLastObj) >= moment(currentPlus3)) {
              fillDates = fillMissingDates(response.data, currentMinus3, responseLastObj);
            }
            else {
              fillDates = fillMissingDates(response.data, currentMinus3, currentPlus3);
            }
          }
          else {
            fillDates = fillMissingDates(response.data, currentMinus3, forecastUsualEnd);
          }
          const convert_response = fillDates?.length>0 && fillDates?.map((item: any) => {
            let oneHrFMColors = backgroundColor(item.one_hr_tl_fuel_moisture===null? -500000: Number(item.one_hr_tl_fuel_moisture_min), oneDeadFuelColors);
            let tenHrFMColors = backgroundColor(item.ten_hr_tl_fuel_moisture===null? -500000: Number(item.ten_hr_tl_fuel_moisture_min), tenDeadFuelColors);
            let hunHrFMColors = backgroundColor(item.hun_hr_tl_fuel_moisture===null? -500000: Number(item.hun_hr_tl_fuel_moisture_min), hunDeadFuelColors);
            let thouHrFMColors = backgroundColor(item.thou_hr_tl_fuel_moisture===null? -500000: Number(item.thou_hr_tl_fuel_moisture_min), thouDeadFuelColors);
            let igColors = backgroundColor(item.ignition_component===null? -500000: Number(item.ignition_component_max), noColors);
            let scColors = backgroundColor(item.spread_component===null? -500000: Number(item.spread_component_max), noColors);
            let ercColors = backgroundColor(item.energy_release_component===null? -500000: Number(item.energy_release_component_max), noColors);
            let biColors = backgroundColor(item.burning_index===null? -500000: Number(item.burning_index_max), noColors);
            let herbColors = backgroundColor(item.herbaceous_lfi_fuel_moisture===null? -500000: Number(item.herbaceous_lfi_fuel_moisture), herbFMColors);
            let woodyColors = backgroundColor(item.woody_lfi_fuel_moisture===null? -500000: Number(item.woody_lfi_fuel_moisture), woodyFMColors);
            let gsiColors_here = backgroundColor(item.gsi===null? -500000: Number(item.gsi), gsiColors);
            let kbdiColors_here = backgroundColor(item.gsi===null? -500000: Number(item.kbdi), kbdiColors);

            return ({
              ...item,
              dateNum: Number(moment(item.summary_date)),
              oneFMbg: oneHrFMColors.bg,
              oneFMtxt: oneHrFMColors.text,
              tenFMbg: tenHrFMColors.bg,
              tenFMtxt: tenHrFMColors.text,
              hunFMbg: hunHrFMColors.bg,
              hunFMtxt: hunHrFMColors.text,
              thouFMbg: thouHrFMColors.bg,
              thouFMtxt: thouHrFMColors.text,
              igBg: igColors.bg,
              igTxt: igColors.text,
              scBg: scColors.bg,
              scTxt: scColors.text,
              ercBg: ercColors.bg,
              ercTxt: ercColors.text,
              biBg: biColors.bg,
              biTxt: biColors.text,
              herbBg: herbColors.bg,
              herbTxt: herbColors.text,
              woodyBg: woodyColors.bg,
              woodTxt: woodyColors.text,
              gsiBg: gsiColors_here.bg,
              gsiTxt: gsiColors_here.text,
              kbdiBg: kbdiColors_here.bg,
              kbdiTxt: kbdiColors_here.text,
            })
          });
          const sorted = convert_response.sort(({ dateNum: a }, { dateNum: b }) => a - b);
          setModifiedContents(sorted);
        }
        else{
          setNfdrsDailyFailCount(0)
          const forecastUsualEnd =moment().add(7,'days').utc().format('YYYY-MM-DD')

          let fillDates: any;
          if (moment(forecastUsualEnd) >= moment(currentPlus3)) {
              fillDates = fillMissingDates(response.data, currentMinus3, currentPlus3);

          }
          else {
            fillDates = fillMissingDates(response.data, currentMinus3, forecastUsualEnd);
          }
          const convert_response = fillDates?.length>0 && fillDates?.map((item: any) => {
            let oneHrFMColors = backgroundColor(item.one_hr_tl_fuel_moisture===null? -500000: Number(item.one_hr_tl_fuel_moisture_min), oneDeadFuelColors);
            let tenHrFMColors = backgroundColor(item.ten_hr_tl_fuel_moisture===null? -500000: Number(item.ten_hr_tl_fuel_moisture_min), tenDeadFuelColors);
            let hunHrFMColors = backgroundColor(item.hun_hr_tl_fuel_moisture===null? -500000: Number(item.hun_hr_tl_fuel_moisture_min), hunDeadFuelColors);
            let thouHrFMColors = backgroundColor(item.thou_hr_tl_fuel_moisture===null? -500000: Number(item.thou_hr_tl_fuel_moisture_min), thouDeadFuelColors);
            let igColors = backgroundColor(item.ignition_component===null? -500000: Number(item.ignition_component_max), noColors);
            let scColors = backgroundColor(item.spread_component===null? -500000: Number(item.spread_component_max), noColors);
            let ercColors = backgroundColor(item.energy_release_component===null? -500000: Number(item.energy_release_component_max), noColors);
            let biColors = backgroundColor(item.burning_index===null? -500000: Number(item.burning_index_max), noColors);
            let herbColors = backgroundColor(item.herbaceous_lfi_fuel_moisture===null? -500000: Number(item.herbaceous_lfi_fuel_moisture), herbFMColors);
            let woodyColors = backgroundColor(item.woody_lfi_fuel_moisture===null? -500000: Number(item.woody_lfi_fuel_moisture), woodyFMColors);
            let gsiColors_here = backgroundColor(item.gsi===null? -500000: Number(item.gsi), gsiColors);
            let kbdiColors_here = backgroundColor(item.gsi===null? -500000: Number(item.kbdi), kbdiColors);

            return ({
              ...item,
              dateNum: Number(moment(item.summary_date)),
              oneFMbg: oneHrFMColors.bg,
              oneFMtxt: oneHrFMColors.text,
              tenFMbg: tenHrFMColors.bg,
              tenFMtxt: tenHrFMColors.text,
              hunFMbg: hunHrFMColors.bg,
              hunFMtxt: hunHrFMColors.text,
              thouFMbg: thouHrFMColors.bg,
              thouFMtxt: thouHrFMColors.text,
              igBg: igColors.bg,
              igTxt: igColors.text,
              scBg: scColors.bg,
              scTxt: scColors.text,
              ercBg: ercColors.bg,
              ercTxt: ercColors.text,
              biBg: biColors.bg,
              biTxt: biColors.text,
              herbBg: herbColors.bg,
              herbTxt: herbColors.text,
              woodyBg: woodyColors.bg,
              woodTxt: woodyColors.text,
              gsiBg: gsiColors_here.bg,
              gsiTxt: gsiColors_here.text,
              kbdiBg: kbdiColors_here.bg,
              kbdiTxt: kbdiColors_here.text,
            })
          });
          const sorted = convert_response.sort(({ dateNum: a }, { dateNum: b }) => a - b);
          setModifiedContents(sorted);
        }
      } catch (error) {
        console.log('error', error)
        setNfdrsDailyFailCount(counter+1)
      }
    })();
  }, [addAlert, state.currentSliderDate, id, state?.user, 
    setNfdrsDailyFailCount, fillMissingDates, state.nfdrFuelModel]);

  useEffect(() => {
    if(stationMeta?.time_zone_offset){
      nfdrMinMaxCallback(nfdrsDailyFailCount);
    }
    if(nfdrsDailyFailCount>0 && nfdrsDailyFailCount<4){
      nfdrMinMaxCallback(nfdrsDailyFailCount);
    }
  }, [nfdrMinMaxCallback, stationMeta?.time_zone_offset, nfdrsDailyFailCount]);

  useEffect(() => {
    requestAnimationFrame(scrollTocurrentDate);
  }, [modifiedContents]);

  const scrollTocurrentDate = () =>{
    const element = document.getElementById('currentDate') as HTMLElement | null;
    const container = scrollRef.current;

    if(!element || !container) return;
    const elementRight = element.offsetLeft + element.offsetWidth;
    const containerWidth = container.offsetWidth;
    const left = elementRight - containerWidth;

    container.scrollTo({
      left,
      behavior: "smooth"
    });
  };

  const goToTable = () =>{
    window.open(`${ROUTE_PATHS.table}/fireDanger/${stationMeta.station_id}/daily`, '_blank')
  }
  return (
    <div>
      <Grid row gap="md">
        <Grid desktop={{ col: 8 }} style={{ paddingBottom: '5px' }}>
          <Grid
            desktop={{ col: 12 }} style={{ paddingBottom: '5px' }}>
            <Button
              variant="contained"
              style={{ fontSize: '12px', width: '100%' }}
              onClick={() => scrollTocurrentDate()}
            >
              <KeyboardDoubleArrowLeftIcon /> Scroll to Current Observation <KeyboardDoubleArrowRightIcon />
            </Button>
          </Grid>
        </Grid>
        <Grid desktop={{ col: 4 }} style={{ paddingBottom: '5px' }}>
          <Button onClick={goToTable} style={{ textTransform: 'capitalize'}}>Go To Table <LaunchIcon fontSize='small' /></Button>
        </Grid>
      </Grid>

      <TableContainer ref={scrollRef} style={{ maxHeight: 500, maxWidth: 450, overflowX: "auto", overflowY: 'hidden'}} className="smooth-scrollbar">
        <Table className='table-popup' style={{ tableLayout: "auto" }} >
          {/*header date*/}
          {/*IMPORTANT: these table rows are actually columns- updated in Stations.scss*/}
          <TableHead style={{ height: '0px', position: 'sticky', zIndex: 100, left: 0 }}>

            <TableRow>
              <TableCell style={{ border: '1px solid #bbbbbb', fontWeight: 700, fontSize: '12px', height: '32px', position: 'sticky', zIndex: 50, top: 0, backgroundColor: '#A1A1A1', width: '150px' }}>DATE (LST)</TableCell>
              <TableCell style={{ border: '1px solid #bbbbbb', fontWeight: 700, fontSize: '12px', height: '30px', backgroundColor: '#A1A1A1', width: '150px' }}>Min 1 HR FM/Time</TableCell>
              <TableCell style={{ border: '1px solid #bbbbbb', fontWeight: 700, fontSize: '12px', height: '30px', backgroundColor: '#A1A1A1', width: '150px' }}>Min 10 HR FM/Time</TableCell>
              <TableCell style={{ border: '1px solid #bbbbbb', fontWeight: 700, fontSize: '12px', height: '30px', backgroundColor: '#A1A1A1', width: '150px' }}>Min 100 HR FM/Time</TableCell>
              <TableCell style={{ border: '1px solid #bbbbbb', fontWeight: 700, fontSize: '12px', height: '30px', backgroundColor: '#A1A1A1', width: '150px' }}>Min 1000 HR FM/Time</TableCell>
              <TableCell style={{ border: '1px solid #bbbbbb', fontWeight: 700, fontSize: '12px', height: '30px', backgroundColor: '#A1A1A1', width: '150px' }}>KBDI</TableCell>
              <TableCell style={{ border: '1px solid #bbbbbb', fontWeight: 700, fontSize: '12px', height: '30px', backgroundColor: '#A1A1A1', width: '150px' }}>HERB FM</TableCell>
              <TableCell style={{ border: '1px solid #bbbbbb', fontWeight: 700, fontSize: '12px', height: '30px', backgroundColor: '#A1A1A1', width: '150px' }}>WOODY FM</TableCell>
              <TableCell style={{ border: '1px solid #bbbbbb', fontWeight: 700, fontSize: '12px', height: '30px', backgroundColor: '#A1A1A1', width: '150px' }}>GSI</TableCell>
              <TableCell style={{ border: '1px solid #bbbbbb', fontWeight: 700, fontSize: '12px', height: '30px', backgroundColor: '#A1A1A1', width: '150px' }}>Max IC/Time</TableCell>
              <TableCell style={{ border: '1px solid #bbbbbb', fontWeight: 700, fontSize: '12px', height: '30px', backgroundColor: '#A1A1A1', width: '150px' }}>Max SC/Time</TableCell>
              <TableCell style={{ border: '1px solid #bbbbbb', fontWeight: 700, fontSize: '12px', height: '30px', backgroundColor: '#A1A1A1', width: '150px' }}>Max ERC/Time</TableCell>
              <TableCell style={{ border: '1px solid #bbbbbb', fontWeight: 700, fontSize: '12px', height: '33px', backgroundColor: '#A1A1A1', width: '150px' }}>Max BI/Time</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow style={{ width: '100px' }} />
            {modifiedContents?.length > 1 &&
              modifiedContents.map((item: any, y) => (
                <TableRow style={{ width: '100px' }}
                  key={`${item.summary_date}-${y}`}
                  id={item.summary_date === (moment(state.currentSliderDate).format('YYYY-MM-DD')) ? 'currentDate' : ''}
                  className={item.summary_date === (moment(state.currentSliderDate).format('YYYY-MM-DD')) ? (item.nfdr_type === 'F' ? 'selected-popup-dashed' : 'selected-popup') : ''}
                >
                  <TableCell style={{ fontSize: '12px', height: '30px', position: 'sticky', zIndex: 50, top: 0, backgroundColor: 'white', width: '100px' }}
                    className={item.observation_type === 'F' ? 'forecast' : 'obs'}
                    key={item.userTZ_display_hour_datetime}>{`${moment(item.summary_date).format('MM/DD/YY')} ${stationMeta?.time_zone}`}</TableCell>


                  <TableCell className={item.nfdr_type === 'F' ? 'forecast' : 'obs'} key={`1hr-${item.one_hr_tl_fuel_moisture_min}--${item.oneFMbg}-${y}`} style={item.oneFMbg ? { height: '30px', backgroundColor: item.oneFMbg, color: item.oneFMtxt } : { height: '30px' }}>{item.one_hr_tl_fuel_moisture_min === null ? "●●●" : roundOneDecimal(item.one_hr_tl_fuel_moisture_min)} / {item.one_hr_tl_fuel_moisture_min_time}:00 </TableCell>
                  <TableCell className={item.nfdr_type === 'F' ? 'forecast' : 'obs'} key={`10hr-${item.ten_hr_tl_fuel_moisture_min}--${item.tenFMbg}-${y}`} style={item.tenFMbg ? { height: '30px', backgroundColor: item.tenFMbg, color: item.tenFMtxt } : { height: '30px' }}>{item.ten_hr_tl_fuel_moisture_min === null ? "●●●" : roundOneDecimal(item.ten_hr_tl_fuel_moisture_min)} / {item.ten_hr_tl_fuel_moisture_min_time}:00 </TableCell>
                  <TableCell className={item.nfdr_type === 'F' ? 'forecast' : 'obs'} key={`100hr-${item.hun_hr_tl_fuel_moisture_min}--${item.hunFMbg}-${y}`} style={item.hunFMbg ? { height: '30px', backgroundColor: item.hunFMbg, color: item.hunFMtxt } : { height: '30px' }}>{item.hun_hr_tl_fuel_moisture_min === null ? "●●●" : roundOneDecimal(item.hun_hr_tl_fuel_moisture_min)} / {item.hun_hr_tl_fuel_moisture_min_time}:00 </TableCell>
                  <TableCell className={item.nfdr_type === 'F' ? 'forecast' : 'obs'} key={`1000hr-${item.thou_hr_tl_fuel_moisture_min}--${item.thouFMbg}-${y}`} style={item.thouFMbg ? { height: '30px', backgroundColor: item.thouFMbg, color: item.thouFMtxt } : { height: '30px' }}>{item.thou_hr_tl_fuel_moisture_min === null ? "●●●" : roundOneDecimal(item.thou_hr_tl_fuel_moisture_min)} / {item.thou_hr_tl_fuel_moisture_min_time}:00 </TableCell>
                  <TableCell className={item.nfdr_type === 'F' ? 'forecast' : 'obs'} key={`kbdi-${item.kbdi}--${item.kbdi}-${y}`} style={item.kbdiBg ? { height: '30px', backgroundColor: item.kbdiBg, color: item.kbdiTxt } : { height: '30px' }}>{item.kbdi === null ? "●●●" : roundOneDecimal(item.kbdi)} </TableCell>
                  <TableCell className={item.nfdr_type === 'F' ? 'forecast' : 'obs'} key={`herb-${item.herbaceous_lfi_fuel_moisture}--${item.herbBg}-${y}`} style={item.herbBg ? { height: '30px', backgroundColor: item.herbBg, color: item.herbTxt } : { height: '30px' }}>{item.herbaceous_lfi_fuel_moisture === null ? "●●●" : roundOneDecimal(item.herbaceous_lfi_fuel_moisture)} </TableCell>
                  <TableCell className={item.nfdr_type === 'F' ? 'forecast' : 'obs'} key={`woody-${item.woody_lfi_fuel_moisture}--${item.woodyBg}-${y}`} style={item.woodyBg ? { height: '30px', backgroundColor: item.woodyBg, color: item.woodTxt } : { height: '30px' }}>{item.woody_lfi_fuel_moisture === null ? "●●●" : roundOneDecimal(item.woody_lfi_fuel_moisture)} </TableCell>
                  <TableCell className={item.nfdr_type === 'F' ? 'forecast' : 'obs'} key={`gsi-${item.gsi}--${item.gsiBg}-${y}`} style={item.gsiBg ? { height: '30px', backgroundColor: item.gsiBg, color: item.gsiTxt } : { height: '30px' }}>{item.gsi === null ? "●●●" : (item.gsi)} </TableCell>
                  <TableCell className={item.nfdr_type === 'F' ? 'forecast' : 'obs'} key={`maxic-${item.ignition_component_max}--${item.igBg}-${y}`} style={item.igBg ? { height: '30px', backgroundColor: item.igBg, color: item.igTxt } : { height: '30px' }}>{item.ignition_component_max === null ? "●●●" : roundOneDecimal(item.ignition_component_max)} / {item.ignition_component_max_time}:00 </TableCell>

                  <TableCell className={item.nfdr_type === 'F' ? 'forecast' : 'obs'} key={`maxsc-${item.spread_component_max}--${item.scBg}-${y}`} style={item.scBg ? { height: '30px', backgroundColor: item.scBg, color: item.scTxt } : { height: '30px' }}>{item.spread_component_max === null ? "●●●" : roundOneDecimal(item.spread_component_max)} / {item.spread_component_max_time}:00 </TableCell>

                  <TableCell className={item.nfdr_type === 'F' ? 'forecast' : 'obs'} key={`maxerc-${item.energy_release_component_max}--${item.ercBg}-${y}`} style={item.ercBg ? { height: '30px', backgroundColor: item.ercBg, color: item.ercTxt } : { height: '30px' }}>{item.energy_release_component_max === null ? "●●●" : roundOneDecimal(item.energy_release_component_max)} / {item.energy_release_component_max_time}:00 </TableCell>

                  <TableCell className={item.nfdr_type === 'F' ? 'forecast' : 'obs'} key={`maxbi-${item.burning_index_max}--${item.biBg}-${y}`} style={item.biBg ? { height: '30px', backgroundColor: item.biBg, color: item.biTxt } : { height: '30px' }}>{item.burning_index_max === null ? "●●●" : roundOneDecimal(item.burning_index_max)} / {item.burning_index_max_time}:00 </TableCell>
                </TableRow>
              ))
            }
          </TableBody>
        </Table>
      </TableContainer>
    </div>

  )
}